import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';
import { resolveContentType } from '@/features/comments/api';
import * as api from './api';
import type { DocumentStatus, TemplateType } from './constants';
import type { TemplateInput } from './types';

export const documentKeys = {
  templates: ['templates'] as const,
  templateLists: () => [...documentKeys.templates, 'list'] as const,
  templateList: (p: api.TemplateListParams) => [...documentKeys.templateLists(), p] as const,
  documents: ['documents'] as const,
  documentLists: () => [...documentKeys.documents, 'list'] as const,
  documentList: (p: api.DocumentListParams) => [...documentKeys.documentLists(), p] as const,
  versions: (id: number) => [...documentKeys.documents, 'versions', id] as const,
  contentType: (label: string) => [...documentKeys.documents, 'ct', label] as const,
  forEntity: (label: string, objectId: string | number) =>
    [...documentKeys.documents, 'entity', label, String(objectId)] as const,
};

// ---- Templates ----
export function useTemplates(params: api.TemplateListParams = {}) {
  return useQuery({
    queryKey: documentKeys.templateList(params),
    queryFn: () => api.listTemplates(params),
    placeholderData: keepPreviousData,
  });
}

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TemplateInput) => api.createTemplate(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: documentKeys.templateLists() });
      toast.success('Template created');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useUpdateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: Partial<TemplateInput> }) =>
      api.updateTemplate(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: documentKeys.templateLists() });
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.deleteTemplate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: documentKeys.templateLists() });
      toast.success('Template deleted');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useRenderTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: api.RenderTemplateInput }) =>
      api.renderTemplate(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: documentKeys.documentLists() });
      toast.success('Document created from template');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

// ---- Documents ----
export function useDocuments(params: api.DocumentListParams = {}) {
  return useQuery({
    queryKey: documentKeys.documentList(params),
    queryFn: () => api.listDocuments(params),
    placeholderData: keepPreviousData,
  });
}

export function useSignDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.signDocument(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: documentKeys.documentLists() });
      toast.success('Document signed');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

// ---- Wave 12: versions -----------------------------------------------------

export function useDocumentVersions(id: number, enabled = true) {
  return useQuery({
    queryKey: documentKeys.versions(id),
    queryFn: () => api.listDocumentVersions(id),
    enabled: enabled && id > 0,
  });
}

export function useEditDocumentContent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: api.EditDocumentContentInput }) =>
      api.editDocumentContent(id, input),
    onSuccess: (_doc, vars) => {
      qc.invalidateQueries({ queryKey: documentKeys.versions(vars.id) });
      qc.invalidateQueries({ queryKey: documentKeys.documentLists() });
      toast.success('Document updated');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useRestoreDocumentVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, versionNumber }: { id: number; versionNumber: number }) =>
      api.restoreDocumentVersion(id, versionNumber),
    onSuccess: (_doc, vars) => {
      qc.invalidateQueries({ queryKey: documentKeys.versions(vars.id) });
      qc.invalidateQueries({ queryKey: documentKeys.documentLists() });
      toast.success(`Restored to v${vars.versionNumber}`);
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

// ---- Attachments — documents linked to a CRM (or any) entity -------------

/** Documents attached to one entity, resolved via the model label's ContentType. */
export function useEntityDocuments(modelLabel: string, objectId: string | number) {
  const ct = useQuery({
    queryKey: documentKeys.contentType(modelLabel),
    queryFn: () => resolveContentType(modelLabel),
    enabled: Boolean(modelLabel),
    staleTime: Infinity,
  });
  const ctId = ct.data?.id;

  const list = useQuery({
    queryKey: documentKeys.forEntity(modelLabel, objectId),
    queryFn: () =>
      api
        .listDocuments({ content_type: ctId!, object_id: String(objectId) })
        .then((page) => page.results),
    enabled: Boolean(ctId && objectId),
  });

  return {
    contentTypeId: ctId,
    list,
    isPending: ct.isPending || (Boolean(ctId) && list.isPending),
    isError: ct.isError || list.isError,
  };
}

export function useUploadDocument(modelLabel: string, objectId: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { file: File; name: string; content_type: number }) =>
      api.uploadDocument({ ...input, object_id: String(objectId) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: documentKeys.forEntity(modelLabel, objectId) });
      toast.success('Attachment uploaded');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useDeleteDocument(modelLabel: string, objectId: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.deleteDocument(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: documentKeys.forEntity(modelLabel, objectId) });
      toast.success('Attachment removed');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useSetDocumentStatus(modelLabel: string, objectId: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: DocumentStatus }) =>
      api.setDocumentStatus(id, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: documentKeys.forEntity(modelLabel, objectId) });
      toast.success('Status updated');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useSetDocumentType(modelLabel: string, objectId: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, type }: { id: number; type: TemplateType }) =>
      api.setDocumentType(id, type),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: documentKeys.forEntity(modelLabel, objectId) });
      toast.success('Type updated');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}
